import React, { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import ReceiptViewer from "@/components/ReceiptViewer";

const group4 = (s) => (s || "").replace(/(.{4})/g, "$1 ").trim();

export const RibSection = () => {
  const { t } = useApp();
  const [ribs, setRibs] = useState([]);
  const [view, setView] = useState(null);

  const load = () => api.get("/bank/ribs").then((r) => setRibs(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const copy = async (num) => {
    try { await navigator.clipboard.writeText(num); toast.success(t("rib_copied")); } catch { toast.error(t("copy_failed")); }
  };
  const del = async (id) => {
    if (!window.confirm(t("rib_delete_confirm"))) return;
    try { await api.delete(`/bank/ribs/${id}`); toast.success(t("saved")); load(); }
    catch { toast.error("Erreur"); }
  };

  if (!ribs.length) return null;
  const residence = ribs.find((r) => r.kind === "residence");
  const externes = ribs.filter((r) => r.kind !== "residence");

  return (
    <div className="space-y-3" data-testid="rib-section">
      {residence && (
        <div className="rounded-3xl bg-gradient-to-br from-brand-dark to-brand text-white p-5 shadow-ios" data-testid="rib-residence-card">
          <div className="flex items-center justify-between">
            <p className="text-xs font-bold uppercase tracking-wider text-white/70 flex items-center gap-1.5">
              <Icons.CreditCard className="h-4 w-4" /> {t("rib_residence")}
            </p>
            <div className="flex items-center gap-1">
              {residence.file_url && (
                <button onClick={() => setView(residence.file_url)} data-testid="rib-view-residence"
                  className="h-8 w-8 rounded-full bg-white/10 flex items-center justify-center">
                  <Icons.Eye className="h-3.5 w-3.5" />
                </button>
              )}
              <button onClick={() => del(residence.id)} data-testid="rib-delete-residence"
                className="h-8 w-8 rounded-full bg-white/10 flex items-center justify-center">
                <Icons.Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
          <p className="font-bold mt-2">{residence.holder}</p>
          <p className="text-sm text-white/70">{residence.bank_name}{residence.agency ? ` · ${residence.agency}` : ""}</p>
          <div className="mt-3 rounded-2xl bg-white/10 p-3 flex items-center justify-between gap-2">
            <span dir="ltr" className="font-mono text-sm font-bold tracking-wide break-all" data-testid="rib-residence-number">
              {group4(residence.rib_number)}
            </span>
            <button onClick={() => copy(residence.rib_number)} data-testid="rib-copy-btn"
              className="shrink-0 h-9 px-3 rounded-full bg-white/15 hover:bg-white/25 transition-colors text-xs font-bold flex items-center gap-1">
              <Icons.Copy className="h-3.5 w-3.5" /> {t("rib_copy")}
            </button>
          </div>
          {residence.iban && <p className="text-xs text-white/60 mt-2 font-mono break-all">IBAN : {residence.iban}</p>}
        </div>
      )}

      {externes.length > 0 && (
        <div className="rounded-3xl bg-card border border-border p-4" data-testid="rib-externes-card">
          <p className="text-sm font-bold flex items-center gap-1.5 mb-2">
            <Icons.BookUser className="h-4 w-4 text-majorelle" /> {t("rib_externes")}
          </p>
          {externes.map((r) => (
            <div key={r.id} className="py-2.5 border-t border-border/60 first:border-0" data-testid={`rib-externe-${r.id}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-semibold text-sm truncate">{r.label || r.holder}</p>
                  <p className="text-xs text-muted-foreground truncate">{r.holder}{r.bank_name ? ` · ${r.bank_name}` : ""}</p>
                  <p dir="ltr" className="font-mono text-xs mt-0.5 break-all text-start">{group4(r.rib_number)}</p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {r.file_url && (
                    <button onClick={() => setView(r.file_url)} data-testid={`rib-view-${r.id}`}
                      className="h-8 w-8 rounded-full bg-secondary flex items-center justify-center">
                      <Icons.Eye className="h-3.5 w-3.5" />
                    </button>
                  )}
                  <button onClick={() => copy(r.rib_number)} data-testid={`rib-copy-${r.id}`}
                    className="h-8 w-8 rounded-full bg-secondary flex items-center justify-center">
                    <Icons.Copy className="h-3.5 w-3.5" />
                  </button>
                  <button onClick={() => del(r.id)} data-testid={`rib-delete-${r.id}`}
                    className="h-8 w-8 rounded-full bg-secondary text-terracotta flex items-center justify-center">
                    <Icons.Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {view && <ReceiptViewer path={view} onClose={() => setView(null)} />}
    </div>
  );
};
